import { useCallback, useEffect, useRef, useState } from "react";
import { useAuth } from "@clerk/react";
import type { OpsOverviewResponse } from "./types";

const EVE_HOST: string = import.meta.env.VITE_AMY_EVE_HOST || "http://127.0.0.1:2000";
/** The snapshots change once an hour; a 5-minute poll only picks up a sweep that landed meanwhile. */
export const POLL_INTERVAL_MS = 5 * 60_000;

/**
 * Reads the Success controller's snapshots from amy (GET /eve/v1/ops/overview,
 * Clerk bearer, staff only — the server re-checks the uplodio_member flag).
 * `refresh(true)` asks amy to recompute first (`?refresh=1`: reads Amy's data,
 * writes only `ops`).
 */
export function useOpsOverview(enabled: boolean) {
  const { getToken } = useAuth();
  const [data, setData] = useState<OpsOverviewResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [fetchedAt, setFetchedAt] = useState<number | null>(null);
  const seq = useRef(0);

  const refresh = useCallback(
    async (recompute = false) => {
      const mySeq = ++seq.current;
      setLoading(true);
      try {
        const token = await getToken();
        const response = await fetch(`${EVE_HOST}/eve/v1/ops/overview${recompute ? "?refresh=1" : ""}`, {
          headers: { Authorization: `Bearer ${token ?? ""}` },
        });
        const body = (await response.json().catch(() => null)) as (OpsOverviewResponse & { error?: string }) | null;
        if (mySeq !== seq.current) return;
        if (!response.ok || !body?.ok) {
          setError(body?.error ?? `Could not reach Amy (${response.status})`);
        } else {
          setData(body);
          setError(null);
          setFetchedAt(Date.now());
        }
      } catch (err) {
        if (mySeq !== seq.current) return;
        setError(err instanceof Error ? err.message : "Could not reach Amy");
      } finally {
        if (mySeq === seq.current) setLoading(false);
      }
    },
    [getToken]
  );

  useEffect(() => {
    if (!enabled) return;
    void refresh();
    const id = window.setInterval(() => {
      if (document.visibilityState === "visible") void refresh();
    }, POLL_INTERVAL_MS);
    const onVisible = () => {
      if (document.visibilityState === "visible") void refresh();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      window.clearInterval(id);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [enabled, refresh]);

  return { data, error, loading, fetchedAt, refresh };
}
